import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { User, IndianRupee, Home, Briefcase, Heart, Download } from "lucide-react";

export default function HLVCalculatorPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/calculator/hlv`;
  const schemas = [
    webpageSchema(
      "Human Life Value (HLV) Calculator | Life Insurance Coverage | ExpertAdvise.in",
      "Calculate your Human Life Value to determine the right life insurance coverage. Income-based and expense-based HLV methods with detailed breakdown.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Calculators", url: `${baseUrl}/calculators` },
      { name: "HLV Calculator", url: pageUrl },
    ]),
  ];
  const [age, setAge] = useState(35);
  const [annualIncome, setAnnualIncome] = useState(15);
  const [annualExpenses, setAnnualExpenses] = useState(8);
  const [outstandingLoans, setOutstandingLoans] = useState(50);
  const [childrenEducation, setChildrenEducation] = useState(30);
  const [spouseIncome, setSpouseIncome] = useState(6);
  const [existingCover, setExistingCover] = useState(20);

  const results = useMemo(() => {
    const incomeMultiplier = age <= 30 ? 20 : age <= 40 ? 18 : age <= 50 ? 15 : 12;
    const incomeBased = annualIncome * incomeMultiplier;
    const expenseBased = (annualExpenses - spouseIncome) * 15;
    const hlv = Math.max(incomeBased, expenseBased) + outstandingLoans + childrenEducation - existingCover;
    return { hlv: Math.max(0, Math.round(hlv)), incomeBased, expenseBased, incomeMultiplier };
  }, [age, annualIncome, annualExpenses, outstandingLoans, childrenEducation, spouseIncome, existingCover]);

  const formatCurrency = useCallback((val: number) => {
    if (val >= 100) return `₹${val} Lakhs`;
    return `₹${val.toLocaleString()} Lakhs`;
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Human Life Value (HLV) Calculator | Life Insurance Coverage | ExpertAdvise.in"
        description="Calculate your Human Life Value to determine the right life insurance coverage. Income-based and expense-based HLV methods with detailed breakdown."
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />
      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-rose-50 text-rose-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">Insurance Calculator</span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">Human Life Value (HLV) Calculator</h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">Calculate the economic value of your life to determine the right amount of life insurance coverage your family needs.</p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-5">Your Profile</h3>
                <div className="space-y-5">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><User className="w-4 h-4 text-primary-500" />Current Age</label>
                    <input type="range" min="18" max="60" value={age} onChange={(e) => setAge(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>18</span><span className="font-semibold text-primary-700">{age} Years</span><span>60</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><IndianRupee className="w-4 h-4 text-primary-500" />Annual Income (₹ Lakhs)</label>
                    <input type="range" min="3" max="100" step="1" value={annualIncome} onChange={(e) => setAnnualIncome(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹3L</span><span className="font-semibold text-primary-700">₹{annualIncome}L</span><span>₹100L</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Heart className="w-4 h-4 text-primary-500" />Annual Expenses (₹ Lakhs)</label>
                    <input type="range" min="1" max="80" step="1" value={annualExpenses} onChange={(e) => setAnnualExpenses(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹1L</span><span className="font-semibold text-primary-700">₹{annualExpenses}L</span><span>₹80L</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Home className="w-4 h-4 text-primary-500" />Outstanding Loans (₹ Lakhs)</label>
                    <input type="range" min="0" max="200" step="5" value={outstandingLoans} onChange={(e) => setOutstandingLoans(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹0</span><span className="font-semibold text-primary-700">₹{outstandingLoans}L</span><span>₹200L</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Briefcase className="w-4 h-4 text-primary-500" />Children's Education (₹ Lakhs)</label>
                    <input type="range" min="0" max="100" step="5" value={childrenEducation} onChange={(e) => setChildrenEducation(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹0</span><span className="font-semibold text-primary-700">₹{childrenEducation}L</span><span>₹100L</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><IndianRupee className="w-4 h-4 text-primary-500" />Spouse Annual Income (₹ Lakhs)</label>
                    <input type="range" min="0" max="50" step="1" value={spouseIncome} onChange={(e) => setSpouseIncome(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹0</span><span className="font-semibold text-primary-700">₹{spouseIncome}L</span><span>₹50L</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Heart className="w-4 h-4 text-primary-500" />Existing Life Cover (₹ Lakhs)</label>
                    <input type="range" min="0" max="100" step="5" value={existingCover} onChange={(e) => setExistingCover(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹0</span><span className="font-semibold text-primary-700">₹{existingCover}L</span><span>₹100L</span></div>
                  </div>
                </div>
              </div>
              <button onClick={() => alert("PDF report generation will be enabled after backend integration.")} className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-3 rounded-xl transition-colors"><Download className="w-4 h-4" />Download PDF Report</button>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 md:p-8 text-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div><div className="text-sm text-white/80 mb-1">Human Life Value</div><div className="text-3xl md:text-4xl font-bold">{formatCurrency(results.hlv)}</div></div>
                  <div><div className="text-sm text-white/80 mb-1">Additional Cover Needed</div><div className="text-3xl md:text-4xl font-bold">{formatCurrency(Math.max(0, results.hlv - existingCover))}</div></div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/20 grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><div className="text-xs text-white/60 mb-1">Income Multiplier</div><div className="font-semibold">{results.incomeMultiplier}x</div></div>
                  <div><div className="text-xs text-white/60 mb-1">Income Component</div><div className="font-semibold">{formatCurrency(results.incomeBased)}</div></div>
                  <div><div className="text-xs text-white/60 mb-1">Expense Component</div><div className="font-semibold">{formatCurrency(results.expenseBased)}</div></div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">How HLV is Calculated</h3>
                <div className="space-y-3 text-sm">
                  <div className="p-4 bg-slate-50 rounded-xl"><div className="font-semibold text-slate-900">1. Income-Based Method</div><div className="text-slate-500 mt-1">Annual Income × {results.incomeMultiplier} (age-appropriate multiplier) = {formatCurrency(results.incomeBased)}</div></div>
                  <div className="p-4 bg-slate-50 rounded-xl"><div className="font-semibold text-slate-900">2. Expense-Based Method</div><div className="text-slate-500 mt-1">(Annual Expenses − Spouse Income) × 15 years = {formatCurrency(results.expenseBased)}</div></div>
                  <div className="p-4 bg-slate-50 rounded-xl"><div className="font-semibold text-slate-900">3. Add Liabilities</div><div className="text-slate-500 mt-1">Outstanding Loans + Children's Education = {formatCurrency(outstandingLoans + childrenEducation)}</div></div>
                  <div className="p-4 bg-slate-50 rounded-xl"><div className="font-semibold text-slate-900">4. Subtract Existing Cover</div><div className="text-slate-500 mt-1">Total HLV − Existing Insurance = {formatCurrency(Math.max(0, results.hlv - existingCover))} additional needed</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">Get the Right Term Insurance Cover</h2>
          <p className="text-slate-600 mb-6">Don't leave your family's future to chance. Our advisors help you choose the perfect term plan at the best premium.</p>
          <Link to="/term-insurance" className="inline-block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap">Compare Term Plans</Link>
        </div>
      </section>
      <Footer /><FloatingCTA />
    </div>
  );
}